/**
 * Cluster (topic) taxonomy for the /resources hub.
 *
 * The article kicker (e.g. "PFI · Hand-back readiness") is the canonical
 * signal — the first token before the middle dot names the cluster.
 */
import { ShieldCheck, Building2, HardHat, FileText, Layers } from 'lucide-react';

export const CLUSTERS = {
  regulation: {
    slug: 'regulation',
    label: 'Regulation',
    kickerMatches: ['Regulation'],
    icon: ShieldCheck,
    accent: 'text-purple-600 bg-purple-50 border-purple-100',
    title: 'Regulation — Building Safety Act, CDM, clinical safety',
    description:
      'How UK safety, health and compliance regimes actually land on live projects — Building Safety Act 2022, CDM 2015, HTM/SHTM, HSG274, DCB0129/0160.',
  },
  pfi: {
    slug: 'pfi',
    label: 'PFI',
    kickerMatches: ['PFI'],
    icon: Building2,
    accent: 'text-blue-600 bg-blue-50 border-blue-100',
    title: 'PFI — hand-back, lifecycle funds, variation control',
    description:
      'Practitioner notes for SPVs and public authorities: hand-back readiness, lifecycle fund reconciliation, availability disputes, variation ledgers and refinancing diligence.',
  },
  'healthcare-fm': {
    slug: 'healthcare-fm',
    label: 'Healthcare FM',
    kickerMatches: ['Healthcare FM'],
    icon: ShieldCheck,
    accent: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    title: 'Healthcare FM — hard services on NHS estates',
    description:
      'Governance, statutory compliance and mobilisation for hard-services operators on acute and community NHS estates.',
  },
  'nhs-capital': {
    slug: 'nhs-capital',
    label: 'NHS Capital',
    kickerMatches: ['NHS Capital'],
    icon: FileText,
    accent: 'text-cyan-600 bg-cyan-50 border-cyan-100',
    title: 'NHS Capital — business cases and portfolio governance',
    description:
      'Assumptions, options, benefits, risks, costs — how NHS Trust capital teams should structure evidence from SOC to FBC and roll it up across the portfolio.',
  },
  construction: {
    slug: 'construction',
    label: 'Construction',
    kickerMatches: ['Construction'],
    icon: HardHat,
    accent: 'text-orange-600 bg-orange-50 border-orange-100',
    title: 'Construction — Tier-1 delivery, NEC4, CDM, handover',
    description:
      'Stage-gated delivery on Tier-1 UK programmes: NEC4 discipline, CDM 2015 duties, Building Safety Act handover packs, supply chain and mobilisation.',
  },
};

export const CLUSTER_ORDER = ['regulation', 'pfi', 'healthcare-fm', 'nhs-capital', 'construction'];

/** Map an article kicker to a cluster slug (or null if unknown). */
export function clusterFromKicker(kicker = '') {
  const head = kicker.split('·')[0].trim();
  for (const key of CLUSTER_ORDER) {
    if (CLUSTERS[key].kickerMatches.includes(head)) return key;
  }
  return null;
}

/** Icon components keyed by name, for backend-driven articles. */
export const ICON_COMPONENTS = { ShieldCheck, Building2, HardHat, FileText, Layers };

/** Augment a backend article with derived helpers. */
export function decorateArticle(a) {
  return {
    ...a,
    cluster: clusterFromKicker(a.kicker),
    iconComponent: ICON_COMPONENTS[a.icon] || FileText,
  };
}

export function countByCluster(articles) {
  const counts = {};
  for (const key of CLUSTER_ORDER) counts[key] = 0;
  for (const a of articles) {
    const c = clusterFromKicker(a.kicker);
    if (c && counts[c] !== undefined) counts[c] += 1;
  }
  return counts;
}

export const FallbackIcon = Layers;
